import React, { useState } from 'react';
import { Share2 } from 'lucide-react';
import type { Academy, Lesson, Term } from '@court-sync/shared';
import { Button } from '@/components/ui/Button';
import { LessonShareModal } from '@/components/lessons/LessonShareModal';
import { useAcademy } from '@/queries/academy';
import type { LessonEnrollmentRow } from '@/services/lesson';

export interface LessonShareTriggerProps {
  academyId: string;
  lessonId: string;
  lessonName: string;
  lessonType?: Lesson['lessonType'];
  lessonTermId?: string | null;
  lessonIsActive?: boolean;
  term?: Pick<Term, 'id' | 'endDate'> | null;
  enrollments: LessonEnrollmentRow[];
  onAfterNotify?: () => void | Promise<void>;
  disabled?: boolean;
  className?: string;
  label?: string;
  stackLevel?: 'default' | 'elevated';
  /** @deprecated Loaded from academyId when omitted */
  academySlug?: string;
  academy?: Pick<Academy, 'slug' | 'subdomain' | 'subdomainEnabled' | 'subscriptionTier'>;
}

export function LessonShareTrigger({
  academyId,
  lessonId,
  lessonName,
  lessonType,
  lessonTermId,
  lessonIsActive,
  term,
  enrollments,
  onAfterNotify,
  disabled = false,
  className,
  label = 'Share',
  stackLevel = 'elevated',
  academy: academyProp,
}: LessonShareTriggerProps) {
  const [open, setOpen] = useState(false);
  const { academy: fetchedAcademy } = useAcademy(academyId, { enabled: !academyProp });
  const academy = academyProp ?? fetchedAcademy;

  if (!academy?.slug?.trim()) return null;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={className ?? 'gap-1.5'}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <Share2 className="h-4 w-4 shrink-0" aria-hidden />
        {label}
      </Button>
      <LessonShareModal
        isOpen={open}
        onClose={() => setOpen(false)}
        academyId={academyId}
        academy={academy}
        lessonId={lessonId}
        lessonName={lessonName}
        lessonType={lessonType}
        lessonTermId={lessonTermId}
        lessonIsActive={lessonIsActive}
        term={term}
        enrollments={enrollments}
        onAfterNotify={onAfterNotify}
        stackLevel={stackLevel}
      />
    </>
  );
}
